import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Navigation, MapPin } from 'lucide-react';

// Fix Leaflet default marker icon issue
const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = defaultIcon;

interface InteractiveMapProps {
  latitude: number;
  longitude: number;
  onChange: (lat: number, lon: number) => void;
}

export function InteractiveMap({ latitude, longitude, onChange }: InteractiveMapProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  
  const [manualLat, setManualLat] = useState(latitude.toString());
  const [manualLon, setManualLon] = useState(longitude.toString());
  const [error, setError] = useState<string | null>(null);
  const [showInput, setShowInput] = useState(false);

  // Update manual inputs when props change (e.g., user clicks on map)
  useEffect(() => {
    setManualLat(latitude.toFixed(6));
    setManualLon(longitude.toFixed(6));
  }, [latitude, longitude]);

  const handleGoToCoordinates = () => {
    const lat = parseFloat(manualLat);
    const lon = parseFloat(manualLon);

    // Validate coordinates
    if (isNaN(lat) || isNaN(lon)) {
      setError('Please enter valid numbers');
      return;
    }

    if (lat < -90 || lat > 90) {
      setError('Latitude must be between -90 and 90');
      return;
    }

    if (lon < -180 || lon > 180) {
      setError('Longitude must be between -180 and 180');
      return;
    }

    setError(null);
    onChange(lat, lon);
    setShowInput(false); // Close after successful navigation
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleGoToCoordinates();
    }
  };

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    // Initialize map - centered on Abu Dhabi with closer zoom
    const map = L.map(mapContainerRef.current, {
      scrollWheelZoom: true,
      zoomControl: false, // We'll add it manually on the right
    }).setView([latitude, longitude], 10);
    mapRef.current = map;

    // Add zoom control on the bottom right side
    L.control.zoom({
      position: 'bottomright'
    }).addTo(map);

    // Add satellite tile layer (Esri World Imagery)
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      attribution: '&copy; <a href=\"https://www.esri.com/\">Esri</a>',
      maxZoom: 19,
    }).addTo(map);

    // Add labels overlay
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}', {
      attribution: '&copy; <a href=\"https://www.esri.com/\">Esri</a>',
      maxZoom: 19,
    }).addTo(map);

    // Add marker
    const marker = L.marker([latitude, longitude]).addTo(map);
    markerRef.current = marker;

    // Handle map clicks
    map.on('click', (e: L.LeafletMouseEvent) => {
      onChange(e.latlng.lat, e.latlng.lng);
    });

    // Fix map size rendering issue - ensure map container is ready
    const fixMapSize = () => {
      if (mapRef.current && mapRef.current.getContainer()) {
        try {
          mapRef.current.invalidateSize();
        } catch (error) {
          console.warn('Error invalidating map size:', error);
        }
      }
    };
    
    setTimeout(fixMapSize, 100);
    setTimeout(fixMapSize, 300);
    setTimeout(fixMapSize, 500);

    // Cleanup
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
      }
      mapRef.current = null;
      markerRef.current = null;
    };
  }, []); // Only run once on mount

  // Update marker and map view when coordinates change
  useEffect(() => {
    if (!mapRef.current || !markerRef.current) return;

    // Update marker position
    markerRef.current.setLatLng([latitude, longitude]);
    
    // Pan to new location with smooth animation
    mapRef.current.flyTo([latitude, longitude], mapRef.current.getZoom(), {
      duration: 1.5,
      easeLinearity: 0.25
    });
  }, [latitude, longitude]);

  return (
    <div className="relative w-full h-[500px] rounded-lg overflow-hidden border-2 border-slate-300 shadow-lg">
      <div ref={mapContainerRef} className="w-full h-full z-0" />
      
      {/* Manual Coordinate Input Overlay */}
      {showInput && (
        <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm rounded-lg shadow-xl z-[1000] border-2 border-slate-300 p-4 w-80">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#4F819F]" />
              <h3 className="text-sm font-semibold text-slate-900">Enter Coordinates</h3>
            </div>
            <button
              onClick={() => {
                setShowInput(false);
                setError(null);
              }}
              className="text-slate-400 hover:text-slate-600 text-xl leading-none"
            >
              ×
            </button>
          </div>
          
          <div className="space-y-2 mb-3">
            <div>
              <input
                type="text"
                value={manualLat}
                onChange={(e) => setManualLat(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Latitude (e.g., 40.7128)"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#4F819F] focus:border-[#4F819F] transition-all outline-none"
              />
            </div>
            <div>
              <input
                type="text"
                value={manualLon}
                onChange={(e) => setManualLon(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Longitude (e.g., -74.0060)"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#4F819F] focus:border-[#4F819F] transition-all outline-none"
              />
            </div>
          </div>

          {error && (
            <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}

          <button
            onClick={handleGoToCoordinates}
            className="w-full px-4 py-2 bg-[#4F819F] text-white text-sm font-medium rounded-lg hover:bg-[#4E7F9D] transition-colors flex items-center justify-center gap-2"
          >
            <Navigation className="w-4 h-4" />
            Go to Location
          </button>
        </div>
      )}

      {/* Toggle Button for Coordinate Input */}
      {!showInput && (
        <button
          onClick={() => setShowInput(true)}
          className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg z-[1000] border border-slate-200 hover:bg-white hover:border-[#4F819F] transition-all flex items-center gap-2"
        >
          <MapPin className="w-4 h-4 text-[#4F819F]" />
          <span className="text-sm font-semibold text-slate-900">Enter Coordinates</span>
        </button>
      )}
      
      {/* Info overlay */}
      <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg z-[1000] border border-slate-200">
        <p className="text-sm font-semibold text-slate-900">Click on map to select location</p>
        <p className="text-xs text-slate-600 mt-1">
          {latitude.toFixed(4)}°{latitude >= 0 ? 'N' : 'S'}, {Math.abs(longitude).toFixed(4)}°{longitude >= 0 ? 'E' : 'W'}
        </p>
      </div>
      
      {/* Zoom info */}
      <div className="absolute bottom-4 left-4 bg-blue-500/90 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-lg z-[1000] text-white text-xs">
        🛰️ Satellite View
      </div>
    </div>
  );
}